import React, { useEffect, useRef, useState, useCallback } from "react";
import ChevronDown from "../../../assets/icons/Chevron/down_black.svg";
import "../../../styles/scroll.css";
import error from "../../../assets/icons/warning/error_gray.svg";
import { ThinButton } from "../../../assets/components/ButtonGroup";
import { Bottom } from "../../../assets/components/BottomSheetGroup";

const PERIOD_OPTIONS = ["1개월", "3개월", "6개월"];
const TYPE_OPTIONS = ["전체", "결제 완료", "결제 실패"];

const pad2 = (n) => String(n).padStart(2, "0");

// 25.09.28
const formatDateShort = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const yy = String(d.getFullYear()).slice(-2);
    const mm = pad2(d.getMonth() + 1);
    const dd = pad2(d.getDate());
    return `${yy}.${mm}.${dd}`;
};

// 18:00
const formatTimeShort = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";

    // UTC에서 한국 시간으로 변환 (UTC +9:00)
    const offset = 9 * 60; // 한국 시간 (UTC +9) 오프셋, 분 단위
    d.setMinutes(d.getMinutes() + offset);

    const hh = pad2(d.getHours());
    const mi = pad2(d.getMinutes());
    return `${hh}:${mi}`;
};

const PaymentHistory = ({ purchased = [], onClickBuyToken }) => {
    const [visibleCount, setVisibleCount] = useState(10);
    const [showLoader, setShowLoader] = useState(false);
    const loaderRef = useRef(null);

    const [period, setPeriod] = useState("1개월");
    const [typeFilter, setTypeFilter] = useState("전체");

    // "period" | "type" | null
    const [activeSheet, setActiveSheet] = useState(null);


    const handleObserver = useCallback((entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
            setShowLoader(true);
            setTimeout(() => {
                setVisibleCount((prev) => prev + 10);
                setShowLoader(false);
            }, 500);
        }
    }, []);


    // 안전하게 소스 배열 확보
    const source = Array.isArray(purchased) ? purchased : [];

    // 1) 최신순(내림차순) 정렬 (item[4] = 결제일시 문자열)
    const sorted = [...source].sort((a, b) => {
        const da = new Date(a[4]);
        const db = new Date(b[4]);
        return db - da;
    });

    // 2) 기간 필터 (1/3/6개월)
    const monthsMap = { "1개월": 1, "3개월": 3, "6개월": 6 };
    const months = monthsMap[period] ?? 1;

    const startDate = new Date();
    startDate.setMonth(startDate.getMonth() - months);

    const filteredByPeriod = sorted.filter((item) => {
        const dateStr = item[4];
        if (!dateStr) return true;
        const d = new Date(dateStr);
        return d >= startDate;
    });


    // 3) 결제 여부 필터 (item[3] === "fail")
    const filtered = filteredByPeriod.filter((item) => {
        const isFail = String(item[3]).toLowerCase() === "fail";

        if (typeFilter === "결제 완료") {
            return !isFail;
        }
        if (typeFilter === "결제 실패") {
            return isFail;
        }
        // "전체"
        return true;
    });


    // 4) 무한 스크롤용 visible 데이터
    const visiblePayments = filtered.slice(0, visibleCount);
    const hasMore = visibleCount < filtered.length;

    useEffect(() => {
        if (!hasMore) return; // 더 불러올 게 없으면 옵저버 안 붙임

        const target = loaderRef.current;
        if (!target) return;  // 아직 로더가 안 그려졌다면 패스

        const observer = new IntersectionObserver(handleObserver, {
            root: null, // viewport 기준
            threshold: 0.1,
        });

        observer.observe(target);

        return () => {
            observer.unobserve(target);
        };
    }, [handleObserver, hasMore, filtered.length]);


    // ----------------- 결제 내역 전혀 없을 때 (전체) -----------------
    if (source.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[50vh] px-[24px]">
                <img
                    src={error}
                    alt=""
                    className="flex items-center justify-center"
                />
                <div className="text-t4 text-text-default pt-[20px]">
                    결제 내역이 없어요
                </div>
                <div className="text-b2 text-text-subtler pt-[4px]">
                    토큰을 구매한 후 이용해 주세요
                </div>
                <div className="text-b2 text-text-inverse w-[271px]">
                    <ThinButton onClick={onClickBuyToken} className="text-b2">
                        토큰 구매하기
                    </ThinButton>
                </div>
            </div>
        );
    }

    return (
        <div className="px-[16px] text-center">
            <div className="pt-[24px] pb-[20px] flex justify-between items-center">
                <div className="text-t2 text-text-subtle">
                    전체 {filtered.length}
                </div>
                <div className="flex items-center gap-[8px] text-b2">
                    {/* 기간 필터 버튼 */}
                    <button
                        type="button"
                        onClick={() => setActiveSheet("period")}
                        className="flex items-center text-b1 text-text-subtle"
                    >
                        <span>{period}</span>
                        <img src={ChevronDown} alt="기간 선택" className="h-[24px] w-[24px]" />
                    </button>

                    <button
                        type="button"
                        onClick={() => setActiveSheet("type")}
                        className="flex items-center text-b1 text-text-subtle"
                    >
                        <span>{typeFilter}</span>
                        <img src={ChevronDown} alt="결제 여부 선택" className="h-[24px] w-[24px]" />
                    </button>
                </div>
            </div>

            <div id="payment-scroll-container">
                {filtered.length === 0 ? (
                    // 기간 + 결제 여부 필터 기준으로는 아무 것도 없을 때
                    <div className="flex flex-col items-center justify-center min-h-[50vh] px-[24px]">
                        <img
                            src={error}
                            alt=""
                            className="flex items-center justify-center"
                        />
                        <div className="text-t4 text-text-default pt-[20px]">
                            결제 내역이 없어요
                        </div>
                        <div className="text-b2 text-text-subtler pt-[4px]">
                            토큰을 구매한 후 이용해 주세요
                        </div>
                        <div className="text-b2 text-text-inverse w-[271px]">
                            <ThinButton onClick={onClickBuyToken} className="text-b2">
                                토큰 구매하기
                            </ThinButton>
                        </div>
                    </div>
                ) : (
                    <>
                        {visiblePayments.map((item, idx) => {
                            const createdAt = item[4];                // ISO 문자열
                            const dateStr = formatDateShort(createdAt); // 25.09.28
                            const timeStr = formatTimeShort(createdAt); // 18:00

                            const isFail = String(item[3]).toLowerCase() === "fail";


                            return (
                                <div
                                    key={idx}
                                    className="space-y-[8px] rounded-[16px] bg-surface-light-subtle px-[16px] py-[12px] mb-[12px]"
                                >
                                    <div className="flex items-center gap-[10px]">
                                        <div className={
                                            `text-c1 ` + (isFail ? `text-text-subtle` : `text-text-primary`)}>
                                            {isFail ? "결제 실패" : "결제 완료"}
                                        </div>
                                        {createdAt && (
                                            <div className="flex items-center text-c2 text-text-disabled gap-[8px]">
                                                <span>·</span>
                                                <span>{dateStr}</span>   {/* 25.09.28 */}
                                                <span>{timeStr}</span>   {/* 18:00 */}
                                            </div>
                                        )}
                                    </div>
                                    {/* 상품명 쓰고 싶으면 아래 주석 풀어서 사용 */}
                                    {/* <div className="text-b2 text-left text-text-subtle pt-[4px]">
                                    {item[0]}
                                </div> */}
                                    <div
                                        className={
                                            `text-t3 text-left ` +
                                            (isFail ? "text-text-disabled" : "text-text-default")
                                        }
                                    >
                                        토큰 {item[2]}개
                                    </div>
                                    <div
                                        className={
                                            `text-b1 text-left ` +
                                            (isFail
                                                ? "text-text-disabled line-through"
                                                : "text-text-subtler")
                                        }
                                    >
                                        {Number(item[1] || 0).toLocaleString()}원
                                    </div>
                                </div>
                            );
                        })}

                        {hasMore && (
                            <div
                                ref={loaderRef}
                                className="py-4 text-sm text-gray-400"
                            >
                                {showLoader && "로딩 중..."}
                            </div>
                        )}
                    </>
                )}
            </div>


            {/* 공통 BottomSheet */}
            <Bottom
                isOpen={activeSheet !== null}
                onClose={() => setActiveSheet(null)}
                title={activeSheet === "period" ? "기간" : "결제 여부"}
            >
                <div className="space-y-[22px]">
                    {(activeSheet === "period" ? PERIOD_OPTIONS : TYPE_OPTIONS).map(
                        (label) => (
                            <button
                                key={label}
                                type="button"
                                className={
                                    "w-full text-left text-b1 " +
                                    (activeSheet === "period"
                                        ? period === label
                                            ? "text-primary-main"
                                            : "text-text-default"
                                        : typeFilter === label
                                            ? "text-primary-main"
                                            : "text-text-default")
                                }
                                onClick={() => {
                                    if (activeSheet === "period") {
                                        setPeriod(label);
                                    } else if (activeSheet === "type") {
                                        setTypeFilter(label);
                                    }

                                    // 목록 리셋하고
                                    setVisibleCount(10);
                                    // 바텀시트 닫기
                                    setActiveSheet(null);
                                }}
                            >
                                {label}
                            </button>
                        )
                    )}
                </div>
            </Bottom>

        </div>
    );
};

export default PaymentHistory;
